import { skipInitialFormValues } from "@/utils/formUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useGetAgentEnumerations } from "../../../common/api/queries";
import { UUID } from "../../../typings/global";
import { DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS } from "../api";
import { ContractListFilterView } from "../components/views/list/ContractListFilterView";
import { ContractListTableView } from "../components/views/list/ContractListTableView";
import { ContractFilterPageRequest } from "../types";

export const ContractListContainer = () => {
  const navigate = useNavigate();

  const searchParams = useFilterSearchParams<ContractFilterPageRequest>({
    params: [
      "report",
      "orderBy",
      "orderDirections",
      "institutionIds",
      "productIds",
      "signerIds",
      "managerIds",
      "gainerIds",
      "affiliatePartnerIds",
      "clientId",
      "statuses",
      "verificationStatuses",
      "createdAtMin",
      "createdAtMax",
      "createdByKeyword",
      "verifiedAtMin",
      "verifiedAtMax",
      "verifiedByKeyword",
      "mediationReportSignDateMin",
      "mediationReportSignDateMax",
      "effectiveBeginningDateMin",
      "effectiveBeginningDateMax",
      "signDateMin",
      "signDateMax",
      "effectiveBeginningDateMonths",
      "signDateMonths",
      "origins",
      "commissionBatchIds",
      "effectiveEndDateOrLoanMaturityDateMin",
      "effectiveEndDateOrLoanMaturityDateMax",
      "cancellationDateMin",
      "cancellationDateMax",
      "transferredFromOtherBroker",
      "transferredToOtherBrokerDateMin",
      "transferredToOtherBrokerDateMax",
      "annualPremiumOrApprovedAmountMin",
      "annualPremiumOrApprovedAmountMax",
      "investmentFeeTypes",
      "commoditySavingsPlans",
      "vehicleFirstRegistrationYearMin",
      "vehicleFirstRegistrationYearMax",
      "onlyIncludedInVerificationExport",
      "gainerDirectorateIds",
      "gainerAllSubordinateId",
      "gainerDirectSubordinateId",
      "signerDirectorateIds",
      "signerAllSubordinateId",
      "signerDirectSubordinateId",
      "managerDirectorateIds",
      "managerDirectSubordinateId",
      "managerAllSubordinateId",
    ],
    defaultQueryParamsObject: DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS,
  });

  const { data: signers, isLoading: isLoadingSigners } = useGetAgentEnumerations(searchParams.signerIds ?? []);
  const { data: managers, isLoading: isLoadingManagers } = useGetAgentEnumerations(searchParams.managerIds ?? []);
  const { data: gainers, isLoading: isLoadingGainers } = useGetAgentEnumerations(searchParams.gainerIds ?? []);

  const signerAgentIds = useMemo(
    () =>
      [searchParams.signerAllSubordinateId, searchParams.signerDirectSubordinateId].filter((id): id is UUID => !!id),
    [searchParams.signerAllSubordinateId, searchParams.signerDirectSubordinateId],
  );
  const { data: signerAgents, isLoading: isLoadingSignerAgents } = useGetAgentEnumerations(signerAgentIds);

  const gainerAgentIds = useMemo(
    () =>
      [searchParams.gainerAllSubordinateId, searchParams.gainerDirectSubordinateId].filter((id): id is UUID => !!id),
    [searchParams.gainerAllSubordinateId, searchParams.gainerDirectSubordinateId],
  );
  const { data: gainerAgents, isLoading: isLoadingGainerAgents } = useGetAgentEnumerations(gainerAgentIds);

  const managerAgentIds = useMemo(
    () =>
      [searchParams.managerAllSubordinateId, searchParams.managerDirectSubordinateId].filter((id): id is UUID => !!id),
    [searchParams.managerAllSubordinateId, searchParams.managerDirectSubordinateId],
  );
  const { data: managerAgents, isLoading: isLoadingManagerAgents } = useGetAgentEnumerations(managerAgentIds);

  const validatedSearchParams = useMemo((): ContractFilterPageRequest | undefined => {
    const hasAnyParam =
      !!searchParams.signerIds?.length ||
      !!searchParams.managerIds?.length ||
      !!searchParams.gainerIds?.length ||
      !!signerAgentIds.length ||
      !!managerAgentIds.length ||
      !!gainerAgentIds.length;

    const allLoaded =
      !isLoadingSigners &&
      !isLoadingManagers &&
      !isLoadingGainers &&
      !isLoadingSignerAgents &&
      !isLoadingGainerAgents &&
      !isLoadingManagerAgents;

    if (!hasAnyParam) {
      return searchParams;
    }

    if (allLoaded) {
      return {
        ...searchParams,
        signerIds: signers?.map((s) => s.id) ?? [],
        managerIds: managers?.map((m) => m.id) ?? [],
        gainerIds: gainers?.map((g) => g.id) ?? [],
        signerAllSubordinateId: signerAgents?.find((agent) => agent.id === searchParams.signerAllSubordinateId)?.id,
        signerDirectSubordinateId: signerAgents?.find((agent) => agent.id === searchParams.signerDirectSubordinateId)
          ?.id,
        gainerAllSubordinateId: gainerAgents?.find((agent) => agent.id === searchParams.gainerAllSubordinateId)?.id,
        gainerDirectSubordinateId: gainerAgents?.find((agent) => agent.id === searchParams.gainerDirectSubordinateId)
          ?.id,
        managerAllSubordinateId: managerAgents?.find((agent) => agent.id === searchParams.managerAllSubordinateId)?.id,
        managerDirectSubordinateId: managerAgents?.find((agent) => agent.id === searchParams.managerDirectSubordinateId)
          ?.id,
      };
    }

    return undefined;
  }, [
    searchParams,
    signers,
    managers,
    gainers,
    isLoadingSigners,
    isLoadingManagers,
    isLoadingGainers,
    isLoadingSignerAgents,
    isLoadingGainerAgents,
    isLoadingManagerAgents,
    signerAgentIds,
    gainerAgentIds,
    managerAgentIds,
    signerAgents,
    gainerAgents,
    managerAgents,
  ]);

  const updateSearchParams = (params: ContractFilterPageRequest, append?: boolean, replace?: boolean) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...params,
            ...skipInitialFormValues(params, DEFAULT_FILTER_CONTRACTS_QUERY_PARAMS),
          },
          append,
        }),
      },
      { replace },
    );
  };

  return (
    <>
      <ContractListFilterView validatedSearchParams={validatedSearchParams} updateSearchParams={updateSearchParams} />

      <ContractListTableView validatedSearchParams={validatedSearchParams} updateSearchParams={updateSearchParams} />
    </>
  );
};
